// ChallengeType - associates the "ctype" variable returned by the Challenger API to the Challenge functionality the JS should use
export class ChallengeType {
    static readonly EMPTY = new ChallengeType([0]);
    static readonly CAPTCHA = new ChallengeType([1, 3, 7, 15]);
    static readonly VIBER = new ChallengeType([31, 63]);
    static readonly POW = new ChallengeType([127, 128, 129]);
    static readonly SMS = new ChallengeType([255, 511]);
    static readonly COMBINED = new ChallengeType([4095]);
    static readonly HONEYPOT = new ChallengeType([8191]);
    static readonly FAIL = new ChallengeType([16383]);
    static readonly Invalid = new ChallengeType([]);

    private static readonly entries: ChallengeType[] =
        [
            ChallengeType.EMPTY,
            ChallengeType.CAPTCHA,
            ChallengeType.VIBER,
            ChallengeType.POW,
            ChallengeType.SMS,
            ChallengeType.COMBINED,
            ChallengeType.HONEYPOT,
            ChallengeType.FAIL
        ]

    // Mapping of ctype code to internal name
    private static readonly ctypeNames = new Map<number, string>([
        [0, "EMPTY"],
        [1, "CAPTCHA"],
        [3, "PNP"],
        [7, "CONSTANTCAPTCHA"],
        [15, "HIRAGANACAPTCHA"],
        [31, "VIBERPIN"],
        [63, "CONSTANTVIBERPIN"],
        [127, "POW"],
        [128, "POW_201"],
        [129, "POW_301"],
        [255, "SMSPIN"],
        [511, "CONSTANTSMSPIN"],
        [1023, "SMS2FA"],
        [2047, "CONSTANTSMS2FA"],
        [4095, "COMBINED"],
        [8191, "HONEYPOT"],
        [16383, "ALWAYSFAILED"]
    ]);

    private ctypes: number[];

    constructor(ctypes: number[]) {
        this.ctypes = ctypes;
    }

    //valueOf - returns a ChallengeType instance for a provided ctype value
    static valueOf(ctype: number): ChallengeType {
        return ChallengeType.entries.find(type => type.ctypes.includes(ctype)) || ChallengeType.Invalid;
    }

    static nameOf(ctype: number): string {
        let name = ChallengeType.ctypeNames.get(ctype);
        if (!name) name = "INVALID";

        return name;
    }
}

// Cdata - the format of the object returned by the Challenger API's /c endpoint
export interface Cdata {
    status: string | number;
    result: {
        cid: string;
        ctype: number;
    };
    code: string; // In case of error
    message: string; // In case of error
}

// ChallengerData - the format of the data collected from Challenger to send for checking
export class ChallengerData {
    public tracking_id: string;
    public cid: string;
    public cres: string;
    public cpid: string;
    public block_submit = false;

    constructor(
        tracking_id: string,
        cid: string,
        cres: string,
        cpid: string,
        block_submit?: boolean,
    ) {
        this.tracking_id = tracking_id;
        this.cid = cid;
        this.cres = cres;
        this.cpid = cpid;
        if (block_submit) { this.block_submit = block_submit; }
    }
}

export class ChallengerEventData {
    start_time: number;
    mc = 0;
    mm = 0;
    kp = 0;
    ts = 0;
}

export class HoneypotClassName {
    static readonly HONEYPOT_DIV: string = "honeypot-div";
    static readonly HONEYPOT_DIV_INPUTS: string = "honeypot-div-inputs";
    static readonly HONEYPOT_RESULT: string = "honeypot-res";
    static readonly HONEYPOT_SECURITY: string = "honeypot-div-security-check";
    static readonly HONEYPOT_CHECK_REQUIRED: string = "honeypot-check-required";
    static readonly HONEYPOT_EXPLANATION: string = "honeypot-explanation";
    static readonly HONEYPOT_INPUT_NOTABOT: string = "honeypot-field-w8i4k3l";
    static readonly HONEYPOT_LABEL_NOTABOT: string = "honeypot-label-r7i2o5k";
    static readonly HONEYPOT_INPUT_TYPE1: string = "honeypot-field-b2e4r52";
    static readonly HONEYPOT_LABEL_TYPE1: string = "honeypot-label-s9o2p4k";
    static readonly HONEYPOT_INPUT_TYPE2: string = "honeypot-field-j3b1u7p";
    static readonly HONEYPOT_LABEL_TYPE2: string = "honeypot-label-v7g2l1j";
    static readonly HONEYPOT_INPUT_TYPE3: string = "honeypot-field-t0z1m4u";
    static readonly HONEYPOT_LABEL_TYPE3: string = "honeypot-label-7i2f9w";
}

export interface HoneypotStyles {
	honeypotText: string;
	honeypotCheckBackground: string;
	honeypotCheckBorder: string;
	honeypotCheckBackgroundHover: string;
	honeypotCheckBackgroundFocus: string;
	honeypotCheckBorderChecked: string;
	honeypotCheckBackgroundChecked: string;
	honeypotCheckMarker: string;
	honeypotCheckBorderRequired: string;
	honeypotCheckRequired: string;
	honeypotExplanation: string;
    honeypotOutline: string;
    honeypotFontFamily: string;
}

// Enum for CSS custom properties names
export enum HoneypotCssVariables {
  checkboxBgColor = '--notabot-checkbox-bg-color',
  checkboxBgColorHover = '--notabot-checkbox-bg-color-hover',
  checkboxBgColorChecked = '--notabot-checkbox-bg-color-checked',
  checkboxBgColorFocus = '--notabot-checkbox-bg-color-focus',
  checkboxBorderColor = '--notabot-checkbox-border-color',
  checkboxBorderColorChecked = '--notabot-checkbox-border-color-checked',
  checkboxBorderColorRequired = '--notabot-checkbox-border-color-required',
  checkboxCheckmarkSvg = '--notabot-checkbox-checkmark-svg',
}

export class HoneypotConst {
    static readonly INPUT_TYPE_NOT_A_BOT: number = 0;
    static readonly INPUT_TYPE_DISPLAY_NONE: number = 1;
    static readonly INPUT_TYPE_NO_SIZE: number = 2;
    static readonly INPUT_TYPE_OUTOFBOUNDS: number = 3;
    static readonly NOT_A_BOT_UNSELECTED_BORDER_COLOR: string = "#BF0000";
}

export enum HttpStatusCode {
    OK = 200,
    BAD_REQUEST = 400,
    FORBIDDEN = 403,
    TOO_MANY_REQUESTS = 429,
}
